import { PageWrapper } from 'components';
import dayjs from 'dayjs';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetPWCFiatAssetListQuery } from 'services/assets';
import { useLazyGetMerchantsListQuery } from 'services/merchants';
import { MerchantsListQuery } from 'services/merchants/types';
import { getCSVRecords, useGetPwcOrdersListQuery } from 'services/pwcOrders';
import { PwcOrdersListQuery } from 'services/pwcOrders/types';

import { DataTableRow } from 'components/dataTable';
import { CurrencyItem } from 'components/inputs/select/currencySelect/types';
import { SelectItem } from 'components/inputs/select/select';

import RoutePath from 'router/path';

import selectAccount from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountType } from 'types/account';
import { FiatAsset } from 'types/assets';
import { NotificationSeverity } from 'types/notifications';
import { RequestPagination, ServerPagination } from 'types/pagination';
import { SortOrder } from 'types/sort';
import { UserAccessLevel } from 'types/user';

import downloadFile from 'utils/downloadFile';
import { usePersistentFilters } from 'utils/filters';
import formatNumber from 'utils/numbers';
import { decoratePagination, getDefaultPagination, undecoratePagination } from 'utils/pagination';
import { usePersistentPagination } from 'utils/tables/pagination';
import { usePersistentSearch } from 'utils/tables/search';
import { getRequestSorting, useTableSorting } from 'utils/tables/sorting';

import { hasUserS4cRole } from '../view/utils';
import { COLUMN_IDS, KEYS, LABELS } from './keys';
import { OrderList } from './orderList';
import { Filters, OrderDataRow } from './types';
import { makeFilters, prepareFilters } from './utils';

const MERCHANT_FILTER_PAGE_SIZE = 200;

export const PwcOrderListContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pushToast } = useNotifications();
  const [isExporting, setExporting] = useState<boolean>(false);
  const { search, setSearch } = usePersistentSearch(KEYS.SEARCH_DEBOUNCE_TIMEOUT, KEYS.FILTER_KEY);
  const { filters, updateFilters, clearFilters } = usePersistentFilters<Filters>({}, KEYS.FILTER_KEY);
  const { account } = useSelector(selectAccount);
  const userLevel = useSelector(selectUserLevel);
  const isS4cUser = hasUserS4cRole(userLevel);
  const { sorting, onSort } = useTableSorting({
    defaultSorting: {
      field: COLUMN_IDS.DATE,
      direction: SortOrder.desc,
    },
  });

  const [fetchMerchantsList] = useLazyGetMerchantsListQuery();
  const { pagination, changeRowsPerPage, changePage, setPagination } = usePersistentPagination(
    getDefaultPagination(),
    [filters, search, sorting],
    KEYS.FILTER_KEY,
  );
  const pwcOrdersListQuery: PwcOrdersListQuery = useMemo(() => {
    const baseQuery: PwcOrdersListQuery = {
      pagination: undecoratePagination(pagination),
      sort: {
        field: sorting.orderBy,
        direction: sorting.order,
      },
      search: search || undefined,
    };

    baseQuery.filters = prepareFilters(filters || {}, account);

    return baseQuery;
  }, [userLevel, account, pagination, sorting, search, filters]);

  const { data, isFetching } = useGetPwcOrdersListQuery(pwcOrdersListQuery, {
    refetchOnMountOrArgChange: true,
  });
  const showPartnerAccountFilter = account.type !== AccountType.Pwc || !account.uuid;

  const getMerchants = useCallback(
    async (params: {
      search?: string;
      pagination?: RequestPagination;
      ids?: string | string[];
    }): Promise<{ data: SelectItem[]; pagination?: ServerPagination }> => {
      const baseMerchantFilters = {
        pwcEnabled: true,
        ...(userLevel === UserAccessLevel.Partner && account.uuid ? { accountUuids: [account.uuid] } : {}),
      } as MerchantsListQuery['filters'];

      // RemoteSelect preload: getItems({ ids }) — resolve labels via merchants list + filters.uuids.
      const isResolveSelectedOnly =
        params.ids !== undefined && params.ids !== '' && params.pagination === undefined && params.search === undefined;

      if (isResolveSelectedOnly) {
        const rawUuids = Array.isArray(params.ids) ? params.ids : [params.ids];
        const uuids = rawUuids.filter((id): id is string => Boolean(id));
        const resolveQuery: MerchantsListQuery = {
          pagination: { itemsPerPage: MERCHANT_FILTER_PAGE_SIZE, page: 1 },
          sort: getRequestSorting(sorting),
          filters: {
            ...baseMerchantFilters,
            uuids,
          },
        };
        const response = await fetchMerchantsList(resolveQuery);
        if ('data' in response && response.data) {
          return {
            data: response.data.data.map((merchant) => ({
              key: merchant.uuid,
              label: merchant.name,
            })),
            pagination: response.data.pagination,
          };
        }
        return { data: [] };
      }

      const query: MerchantsListQuery = {
        pagination: params.pagination ?? { itemsPerPage: MERCHANT_FILTER_PAGE_SIZE, page: 1 },
        sort: getRequestSorting(sorting),
        search: params.search || undefined,
        filters: baseMerchantFilters,
      };

      const response = await fetchMerchantsList(query);

      if ('data' in response && response.data) {
        return {
          data: response.data.data.map((merchant) => ({
            key: merchant.uuid,
            label: merchant.name,
          })),
          pagination: response.data.pagination,
        };
      }

      return { data: [] };
    },
    [account.uuid, fetchMerchantsList, sorting, userLevel],
  );

  const { data: fiatAssetsData } = useGetPWCFiatAssetListQuery();
  const breadcrumbs = LABELS.BREADCRUMBS.map((item) => t(item));

  useEffect(() => {
    if (data?.pagination) {
      setPagination(decoratePagination(data?.pagination));
    }
  }, [data?.pagination]);

  const orders: OrderDataRow[] = (data?.orders || []).map((order) => {
    const fiatAmount = order.fiatAmount ? formatNumber(Number(order.fiatAmount)) : order.fiatAmount;
    const paidFiatAmount = order.paidFiatAmount ? formatNumber(Number(order.paidFiatAmount)) : order.paidFiatAmount;
    return {
      ...order,
      id: order.uuid,
      fiatAmount,
      paidFiatAmount,
    };
  });

  const exportCSV = async () => {
    setExporting(true);
    try {
      const data = await getCSVRecords({ filters: prepareFilters(filters, account), search, withDeposits: true });

      if (data) {
        const objectUrl = window.URL.createObjectURL(data);
        downloadFile({ name: t(LABELS.CSV_FILE_NAME, { date: dayjs().format(KEYS.DATE_FORMAT) }), source: objectUrl });
      }
    } catch (e: any) {
      pushToast({
        severity: NotificationSeverity.error,
        message: t(LABELS.CSV_DOWNLOAD_ERROR),
      });
    }

    setExporting(false);
  };

  const fiatOptions: CurrencyItem[] = (fiatAssetsData?.assets ?? []).map((asset: FiatAsset) => ({
    key: asset.symbol,
    icon: asset.iconUrl,
    name: asset.name,
    label: asset.symbol,
    code: asset.symbol,
  }));

  const clearSearch = () => setSearch('');
  const navigateToOrder = ({ uuid }: DataTableRow) => navigate(RoutePath.pwc.orderById(uuid));
  const filterSettings = useMemo(() => makeFilters({ fiatOptions, getMerchants }), [fiatOptions, getMerchants]);

  return (
    <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
      <OrderList
        pagination={pagination}
        orders={orders}
        isLoading={isFetching}
        onChangePage={changePage}
        onChangeRowsPerPage={changeRowsPerPage}
        onSort={onSort}
        sorting={sorting}
        clearSearch={clearSearch}
        onSearch={setSearch}
        search={search}
        onRowClick={navigateToOrder}
        exportProps={{
          onExport: exportCSV,
          isLoading: isExporting,
        }}
        filterProps={{
          filterSettings,
          filters,
          onFilterChange: updateFilters,
          onFiltersClear: clearFilters,
        }}
        isS4cUser={isS4cUser}
        showPartnerAccountFilter={showPartnerAccountFilter}
      />
    </PageWrapper>
  );
};
